/**
 * Leadler › Öğrenme view model (C3 learning design section 4, corrected by the critique).
 * The names follow the learning spine: arms (growth.arm_state, arm_stats_weekly),
 * keyword_terms and vocabulary_versions, region_estimates, learning_changes, fit_labels,
 * and the change classes A0 to H3. Numbers come from SQL (views and the learning-weekly
 * report), never from an agent; every rate carries its n and interval, and below the
 * minimum n the panel shows "Tahmin yok" whatever value the server sent.
 *
 * Company-level only: no person names, e-mails or phones, no buyer (Etsy) data. Example
 * domains in the preview data are synthetic (example.test).
 *
 * Panel-side until the API phase moves them to @twin/contracts.
 */

import type { Instant, MicroUsd, RejectReasonCode } from './types.js';

// ---- change classes ---------------------------------------------------------

/**
 * One taxonomy for every loop (design 1.4, critique H2). A0 observe, A1 reduce-only and
 * A2 optimise inside the approved envelope are automatic (A2 only with
 * feature.learning_autoapply); H1 weekly batch, H2 versioned release (PR plus v* tag),
 * H3 T4 (panel hold only).
 */
export const CHANGE_CLASSES = ['A0', 'A1', 'A2', 'H1', 'H2', 'H3'] as const;
export type ChangeClass = (typeof CHANGE_CLASSES)[number];
export type AutoChangeClass = Extract<ChangeClass, 'A1' | 'A2'>;
export type HumanChangeClass = Extract<ChangeClass, 'H1' | 'H2' | 'H3'>;

export const CHANGE_CLASS_LABELS: Readonly<Record<ChangeClass, string>> = Object.freeze({
  A0: 'Gözlem',
  A1: 'Sadece azaltır',
  A2: 'Onaylı sınır içinde',
  H1: 'Haftalık paket',
  H2: 'Sürüm (PR)',
  H3: 'T4 · basılı tut',
});

export const LEARNING_LOOPS = ['L1', 'L2', 'L3', 'L4', 'L5', 'L6', 'L7'] as const;
export type LearningLoop = (typeof LEARNING_LOOPS)[number];

export const LOOP_LABELS: Readonly<Record<LearningLoop, string>> = Object.freeze({
  L1: 'Nerede taranır',
  L2: 'Ne aranır',
  L3: 'Kim iyi lead',
  L4: 'Doğrulama kalitesi',
  L5: 'Ne söylenir',
  L6: 'Ne zaman, ne sıklıkla',
  L7: 'Haftalık gözden geçirme',
});

// ---- estimates --------------------------------------------------------------

export type IntervalMethod = 'wilson' | 'beta_posterior' | 'bootstrap' | 'chapman' | 'none';

/**
 * A number with its sample size and interval. value, low and high are in the metric's
 * unit (a 0 to 1 share for rates). Below minN the panel shows "Tahmin yok".
 */
export interface Estimate {
  value: number | null;
  n: number;
  minN: number;
  low: number | null;
  high: number | null;
  /** Interval level: 0.9 for arm posteriors, 0.95 elsewhere. */
  level: number;
  method: IntervalMethod;
}

/** How a metric is written: a share as "%12", per 10 queries, micro-USD, AUC, a count. */
export type MetricUnit = 'share' | 'per10' | 'per100' | 'usd' | 'auc' | 'count' | 'fraction';

// ---- 1. scorecard -----------------------------------------------------------

export const SCORECARD_IDS = [
  'qualified_per_10_queries',
  'cost_per_qualified',
  'cost_per_kabul',
  'fit_auc',
  'audit_field_error',
  'must_not_deliver',
  'regression',
  'positive_replies_per_100',
  'edit_rate',
] as const;
export type ScorecardId = (typeof SCORECARD_IDS)[number];

export interface ScorecardTile {
  id: ScorecardId;
  label: string;
  unit: MetricUnit;
  /** This week. */
  estimate: Estimate;
  /** The 4 weeks before, oldest first; null where that week had no estimate. */
  trend: (number | null)[];
  /** Which direction is better, for the trend word. */
  better: 'higher' | 'lower';
}

// ---- 2. what changed this week (learning_changes) ---------------------------

/** What an automatic change touched. Suppression and opt-out phrases are never reverted. */
export const LEARNING_OBJECT_TYPES = [
  'suppression',
  'opt_out_phrase',
  'sequence',
  'mailbox',
  'mail_path',
  'gateway_ok',
  'query_run',
  'arm_allocation',
  'arm_prune',
  'cell_order',
  'saturation',
  'template_weight',
  'template_locality',
  'verify_queue_order',
  'campaign_order',
] as const;
export type LearningObjectType = (typeof LEARNING_OBJECT_TYPES)[number];

export interface LearningChange {
  changeId: string;
  loop: LearningLoop;
  changeClass: AutoChangeClass;
  objectType: LearningObjectType;
  objectRef: string;
  /** What changed, in words ("Kol en-AU|brave|T3|canopy"). */
  objectLabel: string;
  before: string;
  after: string;
  /** Why, with the numbers ("20 çalıştırma, 0 başarı, üst sınır %4"). */
  why: string;
  evidence: Estimate | null;
  appliedAt: Instant;
  /** Job that applied it: learn-daily, lead-plan, lead-resolve. */
  appliedBy: string;
  revertedAt: Instant | null;
  revertedBy: string | null;
  /** The server's view. The panel also refuses object types that are never reverted. */
  reversible: boolean;
}

export interface RevertInput {
  changeId: string;
  /** Written reason; required for every revert (it goes to learning_changes). */
  reason: string | null;
  /** Set only by the completed 1.5 s hold; required when the revert loosens. */
  confirmedByHold: boolean;
}

export interface RevertResult {
  changeId: string;
  revertedAt: Instant;
}

// ---- 3. waiting for your approval (one learning_batch) ----------------------

/** New twin.action_types (design 1.4) plus the H3 items of the critique. All never_promote. */
export const LEARNING_ACTION_TYPES = [
  'keyword_activate',
  'keyword_retire',
  'arm_restore',
  'saturation_k_change',
  'scan_share_change',
  'source_stop',
  'sector_stop',
  'audit_rate_change',
  'template_variant_approve',
  'experiment_decide',
  'fit_formula_activate',
  'calibration_activate',
  'playbook_publish',
  'below_cutoff_slots',
  'daily_cap_raise',
] as const;
export type LearningActionType = (typeof LEARNING_ACTION_TYPES)[number];

export const TERM_SLOTS = ['business_type', 'product', 'vehicle', 'modifier'] as const;
export type TermSlot = (typeof TERM_SLOTS)[number];

export const TERM_SLOT_LABELS: Readonly<Record<TermSlot, string>> = Object.freeze({
  business_type: 'İş türü',
  product: 'Ürün',
  vehicle: 'Araç',
  modifier: 'Niteleyici',
});

/** A keyword candidate's evidence (keywords-learn, design L2 and critique M2). */
export interface KeywordEvidence {
  term: string;
  slot: TermSlot;
  locale: string;
  /** The region the term was learned in; it applies only there until re-validated. */
  region: string;
  /** Recomputed lift, df and n on qualified (Q) and not qualified (N) sites. */
  lift: number;
  dfQ: number;
  nQ: number;
  dfN: number;
  nN: number;
  /** Business categories the qualified df spans (at least 2 to be proposed). */
  businessCategories: number;
  /** Vehicle-slot terms must map to a catalog or roadmap vehicle. */
  vehicleMapped: boolean | null;
  /** Three example domains (example.test in the preview). */
  exampleDomains: string[];
  /** Known bias, for example "çeki demiri üreticisi ağırlıklı". */
  biasNote: string | null;
}

export interface LearningProposal {
  proposalId: string;
  actionType: LearningActionType;
  changeClass: HumanChangeClass;
  loop: LearningLoop;
  title: string;
  /** What changes. */
  what: string;
  /** The evidence in words, plus the number it rests on. */
  evidenceText: string;
  evidence: Estimate | null;
  expectedEffect: string;
  risk: string;
  reversible: boolean;
  /** The metric that judges it next Monday. */
  judgedBy: string;
  keyword: KeywordEvidence | null;
  /** H2 only: the release PR that carries it. */
  releasePr: string | null;
}

export interface LearningBatch {
  batchId: string;
  /** twin.approval_batches.kind */
  kind: 'learning_batch';
  title: string;
  week: string;
  createdAt: Instant;
  expiresAt: Instant;
  /** Design target: the batch should take about 10 minutes. */
  targetMinutes: number;
  estimatedMinutes: number;
  /** Changes when any proposal changes; a decision on another hash is refused. */
  payloadHash: string;
  proposals: LearningProposal[];
}

export type LearningDecision = 'approve' | 'reject' | 'defer';

export interface LearningItemDecision {
  proposalId: string;
  decision: LearningDecision;
  /** Required for reject. */
  reasonCode: RejectReasonCode | null;
  /** H3 approve: set only by that item's own completed hold. */
  itemHoldConfirmed: boolean;
}

export interface LearningBatchInput {
  batchId: string;
  payloadHash: string;
  decisions: LearningItemDecision[];
  /** Written reason; required when anything is approved (every approval loosens). */
  reason: string | null;
  /** Set only by the completed batch hold; required when anything is approved. */
  confirmedByHold: boolean;
}

export interface LearningBatchResult {
  batchId: string;
  decidedAt: Instant;
  approved: number;
  rejected: number;
  deferred: number;
}

// ---- 4. arms ----------------------------------------------------------------

export const ARM_STATUSES = ['exploring', 'proven', 'pruned', 'downweighted'] as const;
export type ArmStatus = (typeof ARM_STATUSES)[number];

export const ARM_STATUS_LABELS: Readonly<Record<ArmStatus, string>> = Object.freeze({
  exploring: 'Keşif',
  proven: 'Kanıtlı',
  pruned: 'Budandı',
  downweighted: 'Ağırlığı düştü',
});

/** The search source that ran the arm (growth.query_runs.provider): serper, brave, places, ... */
export type ArmSource = 'serper' | 'brave' | 'places' | (string & {});
export type LocalityKind = 'suburb' | 'industrial_estate' | 'postcode';
export type TermOrigin = 'seed' | 'pilot' | 'learned';

export const LOCALITY_LABELS: Readonly<Record<LocalityKind, string>> = Object.freeze({
  suburb: 'Semt',
  industrial_estate: 'Sanayi bölgesi',
  postcode: 'Posta kodu',
});

export const TERM_ORIGIN_LABELS: Readonly<Record<TermOrigin, string>> = Object.freeze({
  seed: 'Tohum',
  pilot: 'Pilot',
  learned: 'Öğrenilmiş',
});

/** One arm in one region: the arm key has no region, the statistics do (cell as stratum). */
export interface ArmRow {
  /** locale|source|template|terms */
  armKey: string;
  locale: string;
  region: string;
  source: ArmSource;
  template: string;
  terms: string[];
  /** Per-term origin flag: learned when any term is learned. */
  termOrigin: TermOrigin;
  localityKind: LocalityKind;
  /** ok and empty runs only. */
  runs: number;
  successes: number;
  /** Posterior mean with its 90% interval; n is runs. */
  posterior: Estimate;
  status: ArmStatus;
  lastRunAt: Instant | null;
  costPerSuccess: MicroUsd | null;
}

// ---- 5. vocabulary ----------------------------------------------------------

export const TERM_STATUSES = ['candidate', 'active', 'retired'] as const;
export type TermStatus = (typeof TERM_STATUSES)[number];

export const TERM_STATUS_LABELS: Readonly<Record<TermStatus, string>> = Object.freeze({
  candidate: 'Aday',
  active: 'Etkin',
  retired: 'Emekli',
});

export interface TermHistoryEntry {
  at: Instant;
  status: TermStatus;
  note: string;
}

/** One growth.keyword_terms row. */
export interface KeywordTermRow {
  termId: string;
  locale: string;
  slot: TermSlot;
  term: string;
  status: TermStatus;
  origin: TermOrigin;
  /** Learned terms carry their region and apply only there; seed terms have none. */
  region: string | null;
  lift: number | null;
  /** Share of runs with a qualified result, with n runs. */
  yield: Estimate;
  history: TermHistoryEntry[];
}

export interface VocabularyLocale {
  locale: string;
  version: number;
  hash: string;
  active: number;
  candidate: number;
  retired: number;
  learnedYield: Estimate;
  seedYield: Estimate;
  terms: KeywordTermRow[];
}

// ---- 6. cells ---------------------------------------------------------------

export interface SaturationCurve {
  cellId: string;
  region: string;
  /** Cumulative new qualified after each block of queries. */
  points: { queries: number; newQualified: number }[];
  satK: number;
  saturated: boolean;
}

/** rankFrontier components; neighbour yield is shrunk toward the prior (critique C4). */
export interface FrontierComponents {
  neighbourYield: number;
  prior: number;
  /** (k * prior + qualified) / (k + queries) inputs. */
  shrinkK: number;
  neighbourQualified: number;
  neighbourQueries: number;
  /** Lead demand from lead_vehicles 'wants' and country data only, never buyer data. */
  demand: number;
  anchor: number;
  ringDistance: number;
}

export interface FrontierRow {
  cellId: string;
  region: string;
  rank: number;
  score: number;
  /** "neden bu hücre" */
  reason: string;
  components: FrontierComponents;
  /** Drawn from the eligible frontier for the 10% holdout. */
  holdout: boolean;
}

/** Capture-recapture history (growth.region_estimates). Reported only when m >= 7. */
export interface RegionEstimate {
  region: string;
  asOf: Instant;
  method: 'chapman' | 'lincoln_petersen';
  n1: number;
  n2: number;
  m: number;
  estimate: number | null;
  ciLow: number | null;
  ciHigh: number | null;
  reportable: boolean;
}

export interface SectorStatus {
  region: string;
  sector: string;
  judgedCells: number;
  status: 'open' | 'stopped' | 'proposed_stop';
}

export interface CellLearning {
  saturation: SaturationCurve[];
  frontier: FrontierRow[];
  holdoutYield: Estimate;
  frontierYield: Estimate;
  regionEstimates: RegionEstimate[];
  sectors: SectorStatus[];
}

// ---- 7. fit (report only) ---------------------------------------------------

export const FIT_TIERS = ['A', 'B', 'C', 'D'] as const;
export type FitTier = (typeof FIT_TIERS)[number];

export const FIT_LABELS = ['high', 'medium', 'low', 'unfit'] as const;
export type FitLabel = (typeof FIT_LABELS)[number];

export const FIT_LABEL_LABELS: Readonly<Record<FitLabel, string>> = Object.freeze({
  high: 'Yüksek',
  medium: 'Orta',
  low: 'Düşük',
  unfit: 'Uygun değil',
});

export const LABEL_SOURCES = ['sercan', 'audit', 'reverify', 'reply', 'deal'] as const;
export type LabelSource = (typeof LABEL_SOURCES)[number];

export const LABEL_SOURCE_LABELS: Readonly<Record<LabelSource, string>> = Object.freeze({
  sercan: 'Sen',
  audit: 'Denetim',
  reverify: 'Yeniden doğrulama',
  reply: 'Cevap',
  deal: 'Anlaşma',
});

export interface TierOutcomeRow {
  tier: FitTier;
  /** Label counts by fit_labels.label. */
  counts: Record<FitLabel, number>;
  /** Share labelled high or medium, Wilson 95%. */
  good: Estimate;
}

export interface ComponentAssociation {
  component: string;
  /** Difference in good share between high and low component scores, with its interval. */
  association: Estimate;
}

export interface FitVersionRow {
  version: string;
  status: 'active' | 'shadow';
  auc: Estimate;
  top20Precision: Estimate;
}

export interface CatalogGapRow {
  vehicle: string;
  /** Qualified companies that serve it although the catalog lacks it. */
  companies: number;
  of: number;
}

/**
 * A lead to label. On a sampled share of these screens the server sets blind and the
 * panel never shows the fit score or tier (critique H4), even if the server sent them.
 */
export interface FitLabelTask {
  taskId: string;
  /** Synthetic company label in the preview; company-level only. */
  company: string;
  domain: string;
  region: string;
  evidence: string[];
  blind: boolean;
  fitScore: number | null;
  tier: FitTier | null;
}

export interface FitReview {
  /** Always true: fit learning is report only until the fit formula decision (PR #21). */
  reportOnly: true;
  formulaDecisionPending: boolean;
  tierOutcome: TierOutcomeRow[];
  components: ComponentAssociation[];
  versions: FitVersionRow[];
  labelsBySource: { source: LabelSource; n: number }[];
  catalogGap: CatalogGapRow[];
  labelTasks: FitLabelTask[];
}

export interface FitLabelInput {
  taskId: string;
  label: FitLabel;
  /** Echoes the screen: the label was given without the score in view. */
  blind: boolean;
}

// ---- 8. verification quality ------------------------------------------------

export interface DisagreementRow {
  rule: string;
  n: number;
  agentVsReferee: Estimate;
  agentVsSercan: Estimate;
}

export interface FixtureOriginRow {
  origin: 'design' | 'correction' | 'reverify' | 'audit';
  count: number;
  passed: number;
}

export interface GatewayRow {
  /** Generic label ("Ağ geçidi A"); no addresses. */
  gateway: string;
  delivered: number;
  bounced: number;
  okRate: Estimate;
}

export interface VerifierQuality {
  disagreement: DisagreementRow[];
  auditFieldError: Estimate;
  mustNotDeliver: number;
  auditedRecords: number;
  /** Calibration needs about 200 audited fields (about 20 weeks). */
  ece: Estimate;
  brier: Estimate;
  fixtures: FixtureOriginRow[];
  regression: { passed: number; total: number; holdoutPassed: number; holdoutTotal: number };
  /** Corrections not yet turned into a fixture (ogrenilmedi). */
  notLearned: number;
  gateways: GatewayRow[];
}

// ---- 9. mail ----------------------------------------------------------------

export interface VariantMetric {
  code: string;
  delivered: number;
  positiveReplies: number;
  rate: Estimate;
  /** v0.1.258: the arm's text, first mails, firms that clicked as a person and firms that replied. */
  label?: string;
  firstMails?: number;
  clickedFirms?: number;
  replies?: number;
}

export interface ExperimentRow {
  experimentId: string;
  variable: 'subject' | 'opening' | 'cta' | 'length' | 'send_time' | 'cadence' | 'followup_delay';
  country: string;
  step: number;
  status: 'running' | 'decided' | 'inconclusive';
  weeks: number;
  maxWeeks: number;
  variants: VariantMetric[];
  /** P(B better than A); null while n is too small. */
  pBetter: number | null;
  expectedLoss: number | null;
}

export interface ConfusionMatrix {
  labels: string[];
  /** rows: true label, columns: classifier label */
  counts: number[][];
}

export interface AutoStopRow {
  at: Instant;
  what: string;
  why: string;
}

export interface MailLearning {
  sendingEnabled: boolean;
  experiments: ExperimentRow[];
  editRate: Estimate;
  denetciCatchRate: Estimate;
  blindSample: { reviewed: number; flagged: number };
  confusion: ConfusionMatrix;
  autoStops: AutoStopRow[];
}

// ---- 10. data health --------------------------------------------------------

export interface StaleJob {
  job: string;
  lastRunAt: Instant | null;
  expectedEveryHours: number;
  enabled: boolean;
}

export interface DataHealth {
  attributionCoverage: Estimate;
  failedSearchShare: Estimate;
  pendingRewards: number;
  unresolvedResults: number;
  staleJobs: StaleJob[];
}

/** The reply desk's last days (v0.1.52): Sercan's verdicts on reply drafts and his own replies. */
export interface ReplyDeskRecent {
  approvalId: string;
  kind: 'edited' | 'rejected';
  at: Instant;
  reason: RejectReasonCode | null;
  note: string | null;
  title: string;
}

export interface ReplyDesk {
  days: number;
  drafts: number;
  passedFirstTime: number;
  denetciRejected: number;
  approved: number;
  edited: number;
  rejected: number;
  rejectedByReason: { reason: RejectReasonCode; n: number }[];
  ownReplies: number;
  pending: number;
  firstPassRate: Estimate;
  editRate: Estimate;
  recent: ReplyDeskRecent[];
}

// ---- the whole view ---------------------------------------------------------

export interface LearningFlags {
  leadEngine: boolean;
  leadVerifier: boolean;
  outreachSending: boolean;
  /** Board-only; A2 changes happen only when this is on. */
  learningAutoapply: boolean;
}

export interface LearningView {
  asOf: Instant;
  /** ISO week of the learning-weekly report ("2026-W39"). */
  week: string;
  flags: LearningFlags;
  scorecard: ScorecardTile[];
  changes: LearningChange[];
  batch: LearningBatch | null;
  arms: ArmRow[];
  vocabulary: VocabularyLocale[];
  cells: CellLearning;
  fit: FitReview;
  verification: VerifierQuality;
  mail: MailLearning;
  health: DataHealth;
  replyDesk: ReplyDesk;
}
